import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  House, Lightning, Drop, Thermometer, Hammer, HardHat, PaintBrush, Wall,
  Buildings, TreeEvergreen, Wrench, Warning, ArrowRight, ArrowLeft, CheckCircle,
  Clock, Plus, X, CurrencyDollar, CalendarBlank, MapPin, CaretDown, ShieldCheck,
  Users, Star, Camera, Image as ImageIcon, Sparkle
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { LOGO_URL } from '../config/branding';
import { trackConversion } from '../utils/analytics';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';
import LanguageMenu from '../components/LanguageMenu';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROJECT_TYPES = [
  { id: 'kitchen_remodel', label: 'Kitchen Remodel', es: 'Remodelación de Cocina', icon: House, color: 'bg-amber-500' },
  { id: 'bathroom_remodel', label: 'Bathroom Remodel', es: 'Remodelación de Baño', icon: Drop, color: 'bg-blue-500' },
  { id: 'roofing', label: 'Roofing', es: 'Techado', icon: HardHat, color: 'bg-red-600' },
  { id: 'painting', label: 'Painting', es: 'Pintura', icon: PaintBrush, color: 'bg-purple-500' },
  { id: 'flooring', label: 'Flooring', es: 'Pisos', icon: Wall, color: 'bg-teal-500' },
  { id: 'electrical', label: 'Electrical', es: 'Electricidad', icon: Lightning, color: 'bg-amber-400' },
  { id: 'plumbing', label: 'Plumbing', es: 'Plomería', icon: Drop, color: 'bg-cyan-500' },
  { id: 'hvac', label: 'HVAC', es: 'Climatización', icon: Thermometer, color: 'bg-cyan-600' },
  { id: 'carpentry', label: 'Carpentry', es: 'Carpintería', icon: Hammer, color: 'bg-orange-600' },
  { id: 'concrete', label: 'Concrete/Masonry', es: 'Concreto/Albañilería', icon: Buildings, color: 'bg-neutral-600' },
  { id: 'landscaping', label: 'Landscaping', es: 'Jardinería', icon: TreeEvergreen, color: 'bg-green-600' },
  { id: 'deck_patio', label: 'Deck/Patio', es: 'Terraza/Patio', icon: House, color: 'bg-amber-700' },
  { id: 'windows_doors', label: 'Windows & Doors', es: 'Ventanas y Puertas', icon: Wall, color: 'bg-sky-600' },
  { id: 'siding', label: 'Siding', es: 'Revestimiento', icon: House, color: 'bg-stone-500' },
  { id: 'general', label: 'General Contractor', es: 'Contratista General', icon: HardHat, color: 'bg-[#2B4C6F]' },
  { id: 'emergency', label: 'Emergency Repair', es: 'Reparación de Emergencia', icon: Warning, color: 'bg-red-700' },
];

const PROPERTY_TYPES = [
  { id: 'single_family', label: 'Single-Family Home', es: 'Casa Unifamiliar' },
  { id: 'condo', label: 'Condo', es: 'Condominio' },
  { id: 'townhouse', label: 'Townhouse', es: 'Casa Adosada' },
  { id: 'multi_family', label: 'Multi-Family', es: 'Multifamiliar' },
  { id: 'commercial', label: 'Commercial', es: 'Comercial' },
];

const SCOPE_BY_TYPE = {
  kitchen_remodel: ['Cabinets', 'Countertops', 'Backsplash', 'Appliances', 'Flooring', 'Lighting', 'Plumbing', 'Layout Change'],
  bathroom_remodel: ['Tub/Shower', 'Vanity', 'Toilet', 'Tile', 'Flooring', 'Lighting', 'Plumbing', 'Layout Change'],
  roofing: ['Full Replacement', 'Repair', 'Inspection', 'Gutter Work', 'Skylight', 'Flat Roof'],
  painting: ['Interior', 'Exterior', 'Trim', 'Cabinets', 'Deck/Fence', 'Color Consultation'],
  flooring: ['Hardwood', 'Tile', 'LVP/Vinyl', 'Carpet', 'Refinishing', 'Subfloor Repair'],
  electrical: ['Panel Upgrade', 'New Outlets', 'Lighting Install', 'Wiring', 'EV Charger', 'Generator'],
  plumbing: ['Leak Repair', 'Water Heater', 'Drain Cleaning', 'Fixture Install', 'Re-piping', 'Sewer Line'],
  hvac: ['New System', 'Repair', 'Maintenance', 'Duct Work', 'Mini-Split', 'Heat Pump'],
  carpentry: ['Trim', 'Custom Built-ins', 'Doors/Windows', 'Framing', 'Repairs', 'Furniture'],
  concrete: ['Driveway', 'Patio', 'Walkway', 'Foundation', 'Stamped Concrete', 'Repair'],
  landscaping: ['Lawn Care', 'Garden Design', 'Hardscaping', 'Irrigation', 'Tree Service', 'Fencing'],
  deck_patio: ['New Build', 'Repair/Refinish', 'Pergola', 'Railings', 'Stairs', 'Composite Decking'],
  windows_doors: ['Window Replacement', 'Entry Door', 'Patio Door', 'Storm Door', 'Garage Door'],
  siding: ['Vinyl', 'Fiber Cement', 'Wood', 'Repair', 'Painting', 'Insulation'],
  general: ['Multi-trade Project', 'Whole-home Remodel', 'Addition', 'Permits Needed', 'Project Mgmt'],
  emergency: ['Water Damage', 'Storm Damage', 'Burst Pipe', 'Power Outage', 'Roof Leak', 'Other'],
};

const TIMELINES = [
  { id: 'asap', label: 'ASAP', es: 'Urgente', sub: 'Emergency / urgent', sub_es: 'Emergencia / urgente' },
  { id: '1-2_weeks', label: 'This Week', es: 'Esta Semana', sub: 'Within 1–2 weeks', sub_es: 'En 1–2 semanas' },
  { id: '1_month', label: 'This Month', es: 'Este Mes', sub: 'Within 30 days', sub_es: 'En 30 días' },
  { id: 'flexible', label: 'Flexible', es: 'Flexible', sub: 'No rush', sub_es: 'Sin prisa' },
];

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export default function HomeownerFunnel() {
  const navigate = useNavigate();
  const { lang } = useWorkerLang();
  const L = (en, es) => (lang === 'es' ? es : en);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState({ verified_contractors: 0, open_jobs: 0, homeowners_served: 0 });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = React.useRef(null);

  const [form, setForm] = useState({
    job_type: '',
    property_type: 'single_family',
    title: '',
    description: '',
    scope_items: [],
    photos: [],
    timeline: 'flexible',
    budget_min: '',
    budget_max: '',
    address_line: '',
    city: '',
    state: '',
    zip_code: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
  });

  const isLoggedInHomeowner = !!localStorage.getItem('token') && localStorage.getItem('userType') === 'homeowner';
  const totalSteps = isLoggedInHomeowner ? 4 : 5;
  const progress = (currentStep / totalSteps) * 100;

  useEffect(() => {
    axios.get(`${API_URL}/api/homeowner-funnel/stats`)
      .then(r => { if (r.data?.success) setStats(r.data.stats); })
      .catch(() => {});
  }, []);

  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));
  const toggleScope = (s) => setForm(p => ({ ...p, scope_items: p.scope_items.includes(s) ? p.scope_items.filter(x => x !== s) : [...p.scope_items, s] }));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadingPhoto(true);
    try {
      // Upload sequentially so we can show progressive feedback
      const newUrls = [];
      for (const file of files) {
        if (file.size > 8 * 1024 * 1024) {
          toast({ title: 'File too large', description: `${file.name} exceeds 8 MB`, variant: 'destructive' });
          continue;
        }
        if (!file.type.startsWith('image/')) {
          toast({ title: 'Invalid file', description: `${file.name} is not an image`, variant: 'destructive' });
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        try {
          const res = await axios.post(`${API_URL}/api/homeowner-funnel/upload-photo`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (res.data?.url) {
            newUrls.push(`${API_URL}${res.data.url}`);
          }
        } catch (err) {
          toast({ title: 'Upload failed', description: err.response?.data?.detail || file.name, variant: 'destructive' });
        }
      }
      if (newUrls.length) {
        setForm(p => ({ ...p, photos: [...p.photos, ...newUrls] }));
        toast({ title: `${newUrls.length} photo${newUrls.length > 1 ? 's' : ''} added`, description: 'Looking great!' });
      }
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const validate = () => {
    switch (currentStep) {
      case 1: return !!form.job_type && !!form.property_type;
      case 2: return form.title.trim().length >= 3 && form.description.trim().length >= 15;
      case 3: return !!form.timeline;
      case 4: return !!form.city && !!form.state;
      case 5: return (
        !!form.name.trim() &&
        !!form.email.trim() && form.email.includes('@') &&
        form.password.length >= 6 &&
        form.password === form.confirm_password
      );
      default: return true;
    }
  };

  const nextStep = () => { if (validate() && currentStep < totalSteps) setCurrentStep(currentStep + 1); };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        budget_min: form.budget_min ? Number(form.budget_min) : null,
        budget_max: form.budget_max ? Number(form.budget_max) : null,
      };
      delete payload.confirm_password;
      if (isLoggedInHomeowner) {
        delete payload.name;
        delete payload.email;
        delete payload.password;
      }
      // Apply pending referral code if present
      const pendingRef = localStorage.getItem('pending_referral_code');
      if (pendingRef) payload.referral_code = pendingRef;
      const res = await axios.post(`${API_URL}/api/homeowner-funnel/submit`, payload,
        isLoggedInHomeowner ? { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } } : undefined);
      if (res.data?.success) {
        trackConversion('quote_request');
        localStorage.removeItem('pending_referral_code');
        if (res.data.token && res.data.user) {
          localStorage.setItem('token', res.data.token);
          localStorage.setItem('userType', res.data.user.user_type);
          localStorage.setItem('userId', res.data.user._id);
          localStorage.setItem('user', JSON.stringify(res.data.user));
        }
        setResult(res.data);
        setSubmitted(true);
        // Hard redirect so AuthContext / navbar refresh
        setTimeout(() => { window.location.href = '/homeowner-dashboard'; }, 2800);
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to submit', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  if (submitted && result) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center p-4" data-testid="homeowner-funnel-success">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-black shadow-[4px_4px_0px_#000]">
            <CheckCircle size={48} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2">Project Posted!</h1>
          <p className="text-neutral-400 mb-4">Your account is ready and your project is live in the marketplace.</p>
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
              <p className="text-2xl font-black text-[#D95800]">{result.matched_contractors}</p>
              <p className="text-[10px] text-neutral-400 uppercase">Contractors Matched</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
              <p className="text-2xl font-black text-emerald-400">{result.lead_grade}</p>
              <p className="text-[10px] text-neutral-400 uppercase">Lead Grade</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
              <p className="text-2xl font-black text-[#2B4C6F]">{result.lead_score}</p>
              <p className="text-[10px] text-neutral-400 uppercase">Quality Score</p>
            </div>
          </div>
          <p className="text-neutral-500 text-sm mb-6">Redirecting to your dashboard where you'll see incoming quotes...</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => { window.location.href = '/homeowner-dashboard'; }} className="h-12 px-6 bg-[#D95800] hover:bg-[#B84A00] text-white font-bold rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all" data-testid="go-dashboard-btn">Go to Dashboard</button>
            <button onClick={() => navigate('/')} className="h-12 px-6 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg border border-white/20 transition-all" data-testid="return-home-btn">Return Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900" data-testid="homeowner-funnel">
      {/* Header */}
      <header className="bg-neutral-900 border-b-4 border-[#2B4C6F]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src={LOGO_URL} alt="ConstructHelp" className="h-10 w-auto" />
          </div>
          <div className="flex items-center gap-3 text-xs text-neutral-400">
            <span className="hidden sm:flex items-center gap-1"><ShieldCheck size={14} weight="bold" className="text-[#2B4C6F]" /> {stats.verified_contractors} {L('Verified Pros', 'Pros Verificados')}</span>
            <LanguageMenu compact />
            <button
              onClick={() => navigate('/trust')}
              className="flex items-center gap-1.5 bg-[#D95800] text-black font-black uppercase tracking-wide text-[10px] px-3 py-1.5 border-2 border-black shadow-[2px_2px_0px_#000] hover:-translate-y-0.5 transition-transform"
              data-testid="funnel-trust-link"
            >
              <ShieldCheck size={13} weight="fill" /> {L('How we protect you', 'Cómo lo protegemos')}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-neutral-500 dark:text-neutral-400">{L('Step', 'Paso')} {currentStep} {L('of', 'de')} {totalSteps}</span>
            <span className="text-xs font-bold text-[#2B4C6F]">
              {currentStep === 1 && L('Project Type', 'Tipo de Proyecto')}
              {currentStep === 2 && L('Project Details', 'Detalles del Proyecto')}
              {currentStep === 3 && L('Timeline & Budget', 'Plazo y Presupuesto')}
              {currentStep === 4 && L('Location', 'Ubicación')}
              {currentStep === 5 && L('Create Account', 'Crear Cuenta')}
            </span>
          </div>
          <div className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
            <div className="h-full bg-[#2B4C6F] transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* AI assistant shortcut (logged-in users, step 1) */}
        {currentStep === 1 && !!localStorage.getItem('token') && (
          <button
            onClick={() => navigate('/ai-project')}
            className="w-full mb-6 flex items-start sm:items-center gap-3 bg-violet-50 dark:bg-violet-900/20 border border-violet-300 dark:border-violet-800 rounded-2xl px-4 py-3 text-left hover:-translate-y-0.5 transition-transform"
            data-testid="funnel-ai-banner"
          >
            <div className="w-9 h-9 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0">
              <Sparkle size={18} weight="fill" className="text-white" />
            </div>
            <p className="text-sm text-violet-900 dark:text-violet-300">
              <span className="font-bold">{L('Prefer to just describe it?', '¿Prefiere solo describirlo?')}</span>{' '}
              {L('Chat with our AI assistant — it builds and posts your project for you.', 'Chatee con nuestro asistente de IA: crea y publica su proyecto por usted.')}{' '}
              <span className="font-bold underline">{L('Try it', 'Pruébelo')} →</span>
            </p>
          </button>
        )}

        {/* Escrow payment protection strip */}
        <div className="mb-6 flex items-start sm:items-center gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl px-4 py-3" data-testid="funnel-escrow-banner">
          <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={18} weight="fill" className="text-white" />
          </div>
          <p className="text-sm text-emerald-800 dark:text-emerald-300">
            <span className="font-bold">{L('Your money stays protected.', 'Su dinero permanece protegido.')}</span> {L('Pay into secure escrow — funds are only released to your contractor when', 'Pague a un depósito en garantía seguro — los fondos solo se liberan a su contratista cuando')} <span className="font-bold">{L('you approve the work', 'usted aprueba el trabajo')}</span>{L(', milestone by milestone.', ', etapa por etapa.')}
          </p>
        </div>

        <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-3xl p-6 sm:p-8 shadow-sm">

          {/* Step 1: Project Type */}
          {currentStep === 1 && (
            <div data-testid="step-project-type">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 text-center">{L("What's your project?", '¿Cuál es su proyecto?')}</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-base mb-8 text-center">{L('Select the type of work you need done.', 'Seleccione el tipo de trabajo que necesita.')}</p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {PROJECT_TYPES.map(t => (
                  <button key={t.id} onClick={() => update('job_type', t.id)}
                    className={`flex items-center gap-3 p-4 rounded-2xl border-2 bg-white dark:bg-neutral-900 shadow-sm transition-all ${form.job_type === t.id ? 'border-[#2B4C6F] bg-[#2B4C6F]/5 shadow-md' : 'border-neutral-200 dark:border-neutral-600 hover:border-[#2B4C6F]/40'}`}
                    data-testid={`projecttype-${t.id}`}>
                    <div className={`w-10 h-10 ${t.color} rounded-xl flex items-center justify-center`}>
                      <t.icon size={20} weight="bold" className="text-white" />
                    </div>
                    <span className="font-bold text-sm text-neutral-900 dark:text-white text-left">{lang === 'es' ? t.es : t.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs font-bold uppercase text-neutral-500 mb-2">{L('Property Type', 'Tipo de Propiedad')}</p>
              <div className="flex flex-wrap gap-2">
                {PROPERTY_TYPES.map(p => (
                  <button key={p.id} onClick={() => update('property_type', p.id)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 transition-all ${form.property_type === p.id ? 'bg-[#2B4C6F] text-white border-[#2B4C6F]' : 'bg-white dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 hover:border-[#2B4C6F]'}`}
                    data-testid={`property-${p.id}`}>
                    {lang === 'es' ? p.es : p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Project Details */}
          {currentStep === 2 && (
            <div data-testid="step-project-details">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 text-center">{L('Tell us more', 'Cuéntenos más')}</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-base mb-8 text-center">{L('A clear description gets better quotes faster.', 'Una descripción clara consigue mejores cotizaciones más rápido.')}</p>
              <div className="mb-5">
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{L('Project Title', 'Título del Proyecto')} *</label>
                <input value={form.title} onChange={(e) => update('title', e.target.value)} maxLength={120}
                  className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white"
                  placeholder={L('e.g. Master Bathroom Remodel', 'ej. Remodelación de baño principal')} data-testid="title-input" />
              </div>
              <div className="mb-5">
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{L('Project Description', 'Descripción del Proyecto')} * <span className="text-neutral-400 normal-case font-normal">({form.description.trim().length}/15 min)</span></label>
                <textarea value={form.description} onChange={(e) => update('description', e.target.value)} rows={4}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white resize-none"
                  placeholder={L('Describe what you need done. Include dimensions, materials preferences, and any constraints.', 'Describa lo que necesita. Incluya dimensiones, materiales preferidos y cualquier restricción.')} data-testid="description-input" />
              </div>
              {SCOPE_BY_TYPE[form.job_type] && (
                <div className="mb-5">
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-2">{L('Scope of Work (select all that apply)', 'Alcance del Trabajo (seleccione lo que aplique)')}</label>
                  <div className="flex flex-wrap gap-2">
                    {SCOPE_BY_TYPE[form.job_type].map(s => (
                      <button key={s} onClick={() => toggleScope(s)}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 transition-all ${form.scope_items.includes(s) ? 'bg-[#2B4C6F] text-white border-[#2B4C6F]' : 'bg-white dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 hover:border-[#2B4C6F]'}`}
                        data-testid={`scope-${s.replace(/[^a-z0-9]/gi, '-')}`}>
                        {form.scope_items.includes(s) ? <CheckCircle size={12} weight="bold" className="inline mr-1" /> : null}{s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{L('Photos (optional)', 'Fotos (opcional)')} <span className="text-neutral-400 normal-case font-normal">— {L('up to 8MB each', 'hasta 8MB cada una')}</span></label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/gif"
                  multiple
                  onChange={(e) => handleFiles(e.target.files)}
                  className="hidden"
                  data-testid="photo-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="w-full h-24 border-2 border-dashed border-neutral-300 dark:border-neutral-600 hover:border-[#2B4C6F] dark:hover:border-[#2B4C6F] rounded-lg flex flex-col items-center justify-center gap-1 bg-neutral-50 dark:bg-neutral-900 transition-all disabled:opacity-50"
                  data-testid="photo-upload-btn"
                >
                  {uploadingPhoto ? (
                    <>
                      <Clock size={24} weight="bold" className="text-[#2B4C6F] animate-spin" />
                      <span className="text-sm font-bold text-neutral-700 dark:text-neutral-200">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Camera size={24} weight="bold" className="text-[#2B4C6F]" />
                      <span className="text-sm font-bold text-neutral-700 dark:text-neutral-200">{L('Click to upload photos', 'Haga clic para subir fotos')}</span>
                      <span className="text-[11px] text-neutral-500">JPG, PNG, WebP, HEIC {L('up to 8 MB', 'hasta 8 MB')}</span>
                    </>
                  )}
                </button>
                {form.photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="photo-thumbnails">
                    {form.photos.map((p, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-lg border-2 border-black overflow-hidden bg-neutral-200 group">
                        <img src={p} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        <button onClick={() => update('photos', form.photos.filter((_, idx) => idx !== i))}
                          className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center opacity-90 hover:opacity-100"
                          data-testid={`remove-photo-${i}`}>
                          <X size={10} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Timeline & Budget */}
          {currentStep === 3 && (
            <div data-testid="step-timeline-budget">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 text-center">{L('When & how much?', '¿Cuándo y cuánto?')}</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-base mb-8 text-center">{L('Help contractors prioritize and quote accurately.', 'Ayude a los contratistas a priorizar y cotizar con precisión.')}</p>
              <div className="mb-5">
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-2">{L('Timeline', 'Plazo')}</label>
                <div className="grid grid-cols-2 gap-2">
                  {TIMELINES.map(t => (
                    <button key={t.id} onClick={() => update('timeline', t.id)}
                      className={`p-4 rounded-2xl border-2 text-left bg-white dark:bg-neutral-900 shadow-sm transition-all ${form.timeline === t.id ? 'border-[#2B4C6F] bg-[#2B4C6F]/5 shadow-md' : 'border-neutral-200 dark:border-neutral-600 hover:border-[#2B4C6F]/40'}`}
                      data-testid={`timeline-${t.id}`}>
                      <p className="font-bold text-sm text-neutral-900 dark:text-white">{lang === 'es' ? t.es : t.label}</p>
                      <p className="text-[10px] text-neutral-500">{lang === 'es' ? t.sub_es : t.sub}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{L('Estimated Budget Range ($)', 'Presupuesto Estimado ($)')}</label>
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" value={form.budget_min} onChange={(e) => update('budget_min', e.target.value)} placeholder="Min (e.g. 5000)"
                    className="h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white" data-testid="budget-min" />
                  <input type="number" value={form.budget_max} onChange={(e) => update('budget_max', e.target.value)} placeholder="Max (e.g. 15000)"
                    className="h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white" data-testid="budget-max" />
                </div>
                <p className="text-[11px] text-neutral-500 mt-2">{L("Don't know? Leave blank — contractors will provide estimates.", '¿No lo sabe? Déjelo en blanco — los contratistas darán estimaciones.')}</p>
              </div>
            </div>
          )}

          {/* Step 4: Location */}
          {currentStep === 4 && (
            <div data-testid="step-location">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 text-center">{L("Where's the project?", '¿Dónde está el proyecto?')}</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-base mb-8 text-center">{L('Your address stays private until you choose a contractor.', 'Su dirección permanece privada hasta que elija un contratista.')}</p>
              <div className="mb-4">
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{L('Street Address (optional)', 'Dirección (opcional)')}</label>
                <input value={form.address_line} onChange={(e) => update('address_line', e.target.value)}
                  className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white"
                  placeholder="123 Main St" data-testid="address-input" />
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{L('City', 'Ciudad')} *</label>
                  <input value={form.city} onChange={(e) => update('city', e.target.value)}
                    className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white"
                    placeholder="e.g. Austin" data-testid="city-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{L('State', 'Estado')} *</label>
                  <select value={form.state} onChange={(e) => update('state', e.target.value)}
                    className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white" data-testid="state-select">
                    <option value="">{L('Select', 'Seleccionar')}</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{L('ZIP Code', 'Código Postal')}</label>
                <input value={form.zip_code} onChange={(e) => update('zip_code', e.target.value)}
                  className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white"
                  placeholder="e.g. 78701" data-testid="zip-input" />
              </div>
            </div>
          )}

          {/* Step 5: Account Creation */}
          {currentStep === 5 && (
            <div data-testid="step-account">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 dark:text-white mb-2 text-center">{L('Become a member', 'Hágase miembro')}</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-base mb-8 text-center">{L('Set up your account to receive and compare quotes — verification badges show which pros are ID-checked.', 'Cree su cuenta para recibir y comparar cotizaciones — las insignias muestran qué profesionales están verificados.')}</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">{L('Full Name', 'Nombre Completo')} *</label>
                  <input value={form.name} onChange={(e) => update('name', e.target.value)}
                    className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white"
                    placeholder="Jane Smith" data-testid="name-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Email * <span className="text-neutral-400 normal-case font-normal">{L('(this will be your username)', '(será su nombre de usuario)')}</span></label>
                  <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                    className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white"
                    placeholder="jane@example.com" data-testid="email-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Phone</label>
                  <input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)}
                    className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white"
                    placeholder="(555) 123-4567" data-testid="phone-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t-2 border-dashed border-neutral-200 dark:border-neutral-700">
                  <div>
                    <label className="block text-xs font-bold uppercase text-neutral-500 mb-1 mt-2">{L('Password', 'Contraseña')} * <span className="text-neutral-400 normal-case font-normal">{L('(min 6 chars)', '(mín. 6 caracteres)')}</span></label>
                    <input type="password" value={form.password} onChange={(e) => update('password', e.target.value)}
                      className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#2B4C6F] text-neutral-900 dark:text-white"
                      placeholder="••••••••" data-testid="password-input" autoComplete="new-password" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-neutral-500 mb-1 mt-2">{L('Confirm Password', 'Confirmar Contraseña')} *</label>
                    <input type="password" value={form.confirm_password} onChange={(e) => update('confirm_password', e.target.value)}
                      className={`w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border rounded-xl focus:outline-none text-neutral-900 dark:text-white ${
                        form.confirm_password && form.password !== form.confirm_password
                          ? 'border-red-500'
                          : 'border-neutral-300 dark:border-neutral-600 focus:border-[#2B4C6F]'
                      }`}
                      placeholder="••••••••" data-testid="confirm-password-input" autoComplete="new-password" />
                    {form.confirm_password && form.password !== form.confirm_password && (
                      <p className="text-[11px] text-red-500 mt-1 font-bold" data-testid="password-mismatch">{L("Passwords don't match", 'Las contraseñas no coinciden')}</p>
                    )}
                  </div>
                </div>
                {/* Payment protection reassurance at signup moment */}
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-4 mt-2" data-testid="signup-escrow-card">
                  <p className="text-sm font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-2 mb-2">
                    <ShieldCheck size={16} weight="fill" /> {L('Protected by ConstructHelp Escrow', 'Protegido por el Depósito en Garantía de ConstructHelp')}
                  </p>
                  <ul className="space-y-1 text-xs text-emerald-700 dark:text-emerald-400">
                    <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="mt-0.5 flex-shrink-0" /> {L('Your payment sits in secure escrow — never paid upfront to the contractor', 'Su pago queda en garantía segura — nunca se paga por adelantado al contratista')}</li>
                    <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="mt-0.5 flex-shrink-0" /> {L('You release funds milestone by milestone, only when you approve the work', 'Usted libera los fondos etapa por etapa, solo cuando aprueba el trabajo')}</li>
                    <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="mt-0.5 flex-shrink-0" /> {L('Disputes are reviewed by our team before any money moves', 'Las disputas son revisadas por nuestro equipo antes de mover dinero')}</li>
                  </ul>
                </div>
                <p className="text-[11px] text-neutral-500 dark:text-neutral-400 pt-2">{L("By joining, you agree to ConstructHelp's Terms of Service and Privacy Policy. Already a member?", 'Al unirse, acepta los Términos de Servicio y la Política de Privacidad de ConstructHelp. ¿Ya es miembro?')} <button type="button" onClick={() => navigate('/login')} className="text-[#2B4C6F] font-bold hover:underline" data-testid="goto-login-link">{L('Log in', 'Iniciar sesión')}</button></p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center gap-3 mt-10">
            {currentStep > 1 && (
              <button onClick={prevStep} className="flex-1 flex items-center justify-center gap-2 h-14 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold text-base rounded-full shadow-sm active:scale-[0.98] transition-all" data-testid="prev-step-btn">
                <ArrowLeft size={20} weight="bold" /> {L('Back', 'Atrás')}
              </button>
            )}
            {currentStep < totalSteps ? (
              <button onClick={nextStep} disabled={!validate()}
                className={`flex-1 flex items-center justify-center gap-2 h-14 font-bold text-base rounded-full active:scale-[0.98] transition-all ${validate() ? 'bg-[#2B4C6F] hover:bg-[#203A55] text-white shadow-sm' : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 dark:text-neutral-500 cursor-not-allowed'}`}
                data-testid="next-step-btn">
                {L('Continue', 'Continuar')} <ArrowRight size={20} weight="bold" />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={!validate() || submitting}
                className={`flex-1 flex items-center justify-center gap-2 h-14 font-bold text-base rounded-full active:scale-[0.98] transition-all ${validate() && !submitting ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm' : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 dark:text-neutral-500 cursor-not-allowed'}`}
                data-testid="submit-btn">
                {submitting ? <Clock size={20} weight="bold" className="animate-spin" /> : <CheckCircle size={20} weight="bold" />}
                {submitting ? L('Posting your project...', 'Publicando su proyecto...') : L('Post Project & Join', 'Publicar Proyecto y Unirse')}
              </button>
            )}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-8 bg-neutral-100 dark:bg-neutral-800 border-2 border-neutral-300 dark:border-neutral-700 rounded-xl p-5">
          <h3 className="font-bold text-neutral-900 dark:text-white mb-3">{L('Frequently Asked Questions', 'Preguntas Frecuentes')}</h3>
          {(lang === 'es' ? [
            { q: '¿Es gratis para propietarios?', a: 'Sí, 100% gratis. Los contratistas compran acceso a su proyecto.' },
            { q: '¿Cómo funciona la protección de pago?', a: 'Al contratar, su pago va a un depósito en garantía seguro en lugar de directo al contratista. El dinero se libera etapa por etapa — solo cuando usted aprueba el trabajo. Si algo sale mal, nuestro equipo revisa la disputa antes de mover fondos.' },
            { q: '¿Qué tan rápido recibiré cotizaciones?', a: 'Los contratistas son notificados al instante y suelen responder en horas. Recibirá una alerta con cada cotización.' },
            { q: '¿Estoy obligado a contratar?', a: 'Para nada. Compare cotizaciones libremente y contrate solo cuando encuentre la opción correcta.' },
            { q: '¿Está segura mi información de contacto?', a: 'Su dirección permanece privada. Los contratistas solo ven su ciudad/estado hasta que usted decida compartir detalles.' },
          ] : [
            { q: 'Is it free for homeowners?', a: 'Yes, 100% free. Contractors purchase access to your project lead.' },
            { q: 'How does payment protection work?', a: 'When you hire, your payment goes into secure escrow instead of straight to the contractor. Money is released milestone by milestone — only when you approve the work. If something goes wrong, our team reviews the dispute before any funds move.' },
            { q: 'How fast will I get quotes?', a: 'Contractors are notified instantly and typically respond within hours. You get an alert the moment each quote arrives.' },
            { q: 'Am I obligated to hire?', a: 'Not at all. Compare quotes freely and only hire when you find the right fit.' },
            { q: 'Is my contact info safe?', a: 'Your address stays private. Contractors only see your city/state until you choose to share details.' },
          ]).map((faq, i) => (
            <details key={i} className="mb-2 group">
              <summary className="cursor-pointer font-bold text-sm text-neutral-700 dark:text-neutral-300 flex items-center gap-2 py-1">
                <CaretDown size={14} weight="bold" className="text-[#2B4C6F] group-open:rotate-180 transition-transform" /> {faq.q}
              </summary>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 pl-6 pb-2">{faq.a}</p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

